import { Fragment, FC, useEffect, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import {
  RedoxIcdCodeType,
  RedoxProblemType
} from "DataTypes/Services/Patient.type";
import Loader from "Components/Loader";
import NoData from "Components/NoData";
import { PatientInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { SideModalProps } from "DataTypes/Modal";
import RedoxFormatter from "Utils/Redox.Formatter";
import { CommonStyle } from "Components";
import ImgPath from "Constant/Imgs";
import TabPanel from "Components/TabPanel";
import { label } from "Constant/En";
import DateTime from "Utils/DateTime";
import RedoxApi from "Service/Redox.api";
import UpdateProblems from "./UpdateProblems";
import ControlledAccord from "Components/Accordion1/Controlled";

export interface DataActionType {
  data: RedoxProblemType | null;
  action: "ADD" | "EDIT" | "VIEW" | "DELETE" | null;
}

const initailState: DataActionType = { data: null, action: null };

const a11yProps: (index: number) => {
  id: string,
  "aria-controls": string
} = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

const Problems: FC<SideModalProps> = (props) => {
  const { onClose } = props;
  const { patientInfo }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const crudPermission = myInfo.roleId != 6;
  const [problemList, setProblemList] = useState<RedoxProblemType[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dataAction, setDataAction] = useState<DataActionType>(initailState);
  const [openAccordId, setOpenAccordId] = useState<number | null>(null);

  useEffect(() => {
    getProblemData();
    setDataAction(initailState)
  }, [tabValue]);

  const getProblemData: () => void = async () => {
    setLoading(true);
    const payload = { problemActive: tabValue == 0 ? "active" : "history" };
    const problemsRes = await RedoxApi.ProblemSearch({
      id1: patientInfo.id,
      data: payload
    });
    if (problemsRes?.success) {
      setProblemList(RedoxFormatter.ProblemList(problemsRes?.data?.problem));
    }
    setLoading(false);
  };

  const handleCloseLayer2: () => void = () => {
    setDataAction({ data: null, action: null })
  };

  const displayHead: (data: RedoxProblemType) => JSX.Element = (data) => {
    return (
      <Box sx={{ ...sxMedBox }}>
        <Typography variant="subtitle1" sx={{ ...sxMedName }}>
          {data.name || label.naText}
        </Typography>
        {crudPermission && (
          <Box
            display="flex"
            justifyContent={"flex-end"}
            sx={{ "&>div": { ml: 1 } }}
            width="30%"
          >
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "EDIT" })}
            >
              <ImgPath.EditOutlined />
            </Box>
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "DELETE" })}
            >
              <ImgPath.DeleteOutline />
            </Box>
          </Box>
        )}
      </Box>
    )
  }

  const bodyContent: (
    altCodeList: RedoxIcdCodeType[]
  ) => JSX.Element = (altCodeList) => {
    return (
      <Fragment>
        {altCodeList.length > 0 ? altCodeList.map((el) => {
          return (
            <Typography variant="subtitle1" mb={1} key={el.id}>
              {`• ${el.code + " -"} ${el.name}`}
            </Typography>
          );
        }) :
          <NoData />
        }
      </Fragment>
    )
  };

  const ActiveUIContent = (
    <Fragment>
      <Box sx={{ ...sxMain }}>
        {loading ? (
          <Loader />
        ) : problemList.length ? (
          problemList.map((el) => {
            return (
              <ControlledAccord
                key={el.id}
                head={displayHead(el)}
                body={bodyContent(el.altCodes)}
                open={el.id == openAccordId}
                handleClick={() => {
                  setOpenAccordId(el.id == openAccordId ? null : el.id)
                }}
              />
            );
          })
        ) : (
          <NoData />
        )}
      </Box>
    </Fragment>
  );

  const HistoricalUIContent = (
    <Fragment>
      <Box sx={{ ...sxMain }}>
        {loading ? (
          <Loader />
        ) : problemList.length ? (
          problemList.map((el) => {
            return (
              <ControlledAccord
                key={el.id}
                head={
                  <Box width="100%">
                    {displayHead(el)}
                    <Typography variant="subtitle1">
                      Stop Date: {
                        DateTime.ToLocalDate(el.endDate) || label.naText
                      }
                    </Typography>
                  </Box>
                }
                body={bodyContent(el.altCodes)}
                open={el.id == openAccordId}
                handleClick={() => {
                  setOpenAccordId(el.id == openAccordId ? null : el.id)
                }}
              />
            )
          })
        ) : (
          <NoData />
        )}
      </Box>
    </Fragment>
  );

  const Layer1 = (
    <Fragment>
      <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
        <Typography fontWeight="bold">Problems</Typography>
        <Box display={"flex"}>
          {
            crudPermission && (
              <Box
                sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
                onClick={() => setDataAction({ data: null, action: "ADD" })}
              >
                <ImgPath.PlusIcon titleAccess="Add Problems" />
              </Box>
            )
          }
          <Box
            sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
            onClick={() => {
              handleCloseLayer2();
              onClose();
            }}
          >
            <ImgPath.CloseIcon titleAccess="Close" />
          </Box>
        </Box>
      </Box>
      <Box sx={{ ...sxTab }}>
        <Tabs
          value={tabValue}
          onChange={(event, index) => {
            setTabValue(index);
          }}
          aria-label="basic tabs example"
          variant="fullWidth"
          indicatorColor="secondary"
        >
          <Tab label="Active" {...a11yProps(0)} />
          <Tab label="Historical" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <TabPanel value={tabValue} index={0} style={{ ...sxTabPanel }}>
        {ActiveUIContent}
      </TabPanel>
      <TabPanel value={tabValue} index={1} style={{ ...sxTabPanel }}>
        {HistoricalUIContent}
      </TabPanel>
    </Fragment>
  );

  return (
    <Fragment>
      <Box width="30%">{Layer1}</Box>
      <Box width="1%"></Box>
      {dataAction.action && (
        <Box width="64%">
          <UpdateProblems
            dataAction={dataAction}
            onClose={handleCloseLayer2}
            refreshList={getProblemData}
          />
        </Box>
      )}
    </Fragment>
  );
};
export default Problems;

const sxTab = {
  px: 2,
  "&>div": {
    borderBottom: 1,
    borderColor: "custom.main",
  },
};

const sxHeader = {
  justifyContent: "space-between",
  height: "10%",
  px: 2,
};

const sxTabPanel = {
  height: "75%",
};

const sxMain = {
  height: "100%",
  overflow: "auto",
  px: 2,
};

const sxMedBox = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%",
  py: 1
};

const sxMedName = {
  fontWeight: "bold",
  width: "80%",
  lineBreak: "anywhere",
};

